import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { ColumnSemantic, Presentation } from "@lala/harness/protocol";

import { ChartLazy } from "../components/ChartLazy.tsx";

const METRIC_SEMANTICS = new Set<ColumnSemantic>([
  "currency",
  "percentage",
  "duration_days",
  "count",
]);

/**
 * `intent: "timeseries"`: línea, con su tabla de respaldo siempre disponible
 * (AGENTS.md: "una serie temporal nunca va sola"). `<Chart>` ya la da con su
 * toggle "ver tabla".
 *
 * El protocolo no distingue línea de área -- `type: "line"` es la elección
 * razonable por defecto (ver resumen final). Se delega en `<ChartLazy>`, la
 * misma que usa un `ui_component` de la herramienta: ECharts solo carga
 * cuando aparece el primer gráfico.
 */
export function Timeseries({ presentation }: { presentation: Presentation }) {
  const categoryColumn = presentation.columns.find(
    (column) => column.semantic === "timestamp" || column.semantic === "label",
  );
  const valueColumns = presentation.columns.filter((column) =>
    METRIC_SEMANTICS.has(column.semantic),
  );

  if (categoryColumn === undefined || valueColumns.length === 0) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Serie temporal mal formada</AlertTitle>
        <AlertDescription>
          No encontré una columna de tiempo (timestamp/label) y al menos una
          numérica.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <ChartLazy
      props={{
        datasetId: presentation.datasetId,
        type: "line",
        title: null,
        categoryKey: categoryColumn.name,
        categoryLabel: categoryColumn.label,
        valueLabel:
          valueColumns.length === 1 ? (valueColumns[0]?.label ?? null) : null,
        series: valueColumns.map((column) => ({
          key: column.name,
          label: column.label,
        })),
        emphasisKey: null,
        rowCount: 0,
      }}
    />
  );
}
